<template>
  <section class="section small">
    <div class="container">
      <ViewSelector :view="view" @change="setView($event)">
        <Title :skipMarginBottom="true">
          Repositories
          <Plus @click="showAdd = true"></Plus>
        </Title>
      </ViewSelector>

      <SearchBar
        v-model="tempFilter"
        :id="searchId"
        :selectOnSubmit="true"
        :loading="searching"
        :focus="false"
        @submit="search($event)"
      ></SearchBar>

      <Tips
        v-if="firstLoadDone && $store.state.source.sources.length !== 0"
        path="sources"
        xKey="dismissedTips"
        property="rightClick"
        :rightClickIconVisible="true"
        >Right-click on a repository name to open the context menu
      </Tips>

      <p
        class="is-italic"
        v-if="firstLoadDone && $store.state.source.sources.length === 0"
      >
        <i>No repos.</i>
      </p>

      <div v-if="firstLoadDone && $store.state.source.sources.length !== 0">
        <View1
          v-if="view == 1"
          :items="filtered"
          @order="dispatchSourceOrder($event)"
          @save="rename($event.source, $event.text)"
          @cancel="toggleRename($event)"
          @menu="$refs.menu.open($event.e, $event.source)"
        ></View1>

        <View2
          v-if="view == 2"
          :items="filtered"
          @order="dispatchSourceOrder($event)"
          @save="rename($event.source, $event.text)"
          @cancel="toggleRename($event)"
          @menu="$refs.menu.open($event.e, $event.source)"
        ></View2>
        <Paging
          v-show="!searching && firstLoadDone"
          :items="paging.pages"
          :selected="paging.page"
          :size="paging.max"
          @change="setPage($event)"
          @size="setPageMax($event)"
          :hasOptions="true"
          autosave="repos"
        ></Paging>
      </div>
    </div>

    <vue-context ref="menu">
      <template v-if="child.data" slot-scope="child">
        <li v-if="child.data.permission < 32 && child.data.permission">
          <a>
            <i>Too low permissions</i>
          </a>
        </li>
        <li v-if="child.data.permission >= 32">
          <a @click.prevent="toggleRename(child.data)">Rename</a>
        </li>

        <li v-if="child.data.permission >= 32">
          <a
            @click.prevent="
              selected = child.data;
              showDelete = true;
            "
            >Delete</a
          >
        </li>
      </template>
    </vue-context>

    <sourceAdd v-if="showAdd" v-model="showAdd">
      <a
        href=""
        @click.stop.prevent="
          showAddExternal = true;
          showAdd = false;
        "
      >
        Add an existing repo instead</a
      >
    </sourceAdd>

    <sourceAddExternal
      v-if="showAddExternal"
      v-model="showAddExternal"
    ></sourceAddExternal>

    <sourceConfirm
      v-if="selected && showDelete"
      v-model="showDelete"
      :source="selected"
      @hide="selected = null"
      @confirmed="_delete(selected)"
    ></sourceConfirm>
  </section>
</template>
<script>
import "vue-context/src/sass/vue-context.scss";
import { postApi } from "@/utils/http";
import { getId } from "@/utils/document";
import { dispatchSourceOrder } from "@/utils/order";
import { getStartIndex, getNumbers } from "@/utils/paging";

export default {
  components: {
    Tips: () => import("@/components/main/Tips.vue"),
    ViewSelector: () => import("@/components/main/ViewSelector.vue"),
    Title: () => import("@/components/singles/Title.vue"),
    VueContext: () => import("vue-context"),
    sourceConfirm: () => import("@/components/main/repo/Delete.vue"),
    sourceAdd: () => import("@/components/main/repo/Add.vue"),
    sourceAddExternal: () => import("@/components/main/repo/AddExternal.vue"),
    EditSingleField: () =>
      import("@/components/singles/EditSingleField/component.vue"),
    Plus: () => import("@/components/main/Plus.vue"),
    SearchBar: () => import("@/components/main/SearchBar.vue"),
    View1: () => import("@/components/main/repo/View1.vue"),
    View2: () => import("@/components/main/repo/View2.vue"),
    Paging: () => import("@/components/main/paging/Nav.vue"),
  },
  props: {},
  computed: {
    view() {
      return this.$store.state.settings.user.sources &&
        this.$store.state.settings.user.sources.view
        ? this.$store.state.settings.user.sources.view
        : 1;
    },
    filtered() {
      let items = this.$store.state.source.sources;

      if (this.filter && this.filter !== "") {
        items = items.filter((x) => x.name.toLowerCase().includes(this.filter));
      }

      this.setPages(items);

      if (this.paging.max === -1) return items;
      else {
        return items.slice(
          this.paging.start,
          this.paging.page * this.paging.max
        );
      }
    },
  },
  data() {
    return {
      selected: null,
      showDelete: false,
      showAdd: false,
      showAddExternal: false,
      saving: false,
      firstLoadDone: false,
      searching: false,
      tempFilter: "",
      filter: "",
      searchId: getId(),
      paging: {
        page: 1,
        max: 15,
        start: 0,
        pages: [],
      },
    };
  },
  async created() {
    try {
      await this.$store.dispatch("source/fetchAll", true);

      await this.$store.dispatch("settings/getUserSetting", {
        path: "repos",
        key: "paging",
      });

      const paging = this.$store.state.settings.user.repos?.paging;
      if (paging && paging.max) this.paging.max = paging.max;

      this.firstLoadDone = true;
    } catch (err) {
      console.log(err);
      this.$helpers.notify("Error", "Could not get sources.", "danger", 5000);
    }
  },
  methods: {
    setPageMax(size) {
      this.paging.max = size;

      this.setPage(1);
      this.setPages(this.$store.state.source.sources);
    },
    setPages(items) {
      this.paging.pages = getNumbers(items, this.paging.max);
    },
    setPage(page) {
      this.paging.start = getStartIndex(page, this.paging.max);
      this.paging.page = page;
    },
    setView(view) {
      this.$store.dispatch("settings/setUser", {
        path: "sources",
        key: "view",
        value: view,
      });
    },
    dispatchSourceOrder: dispatchSourceOrder,
    search(searchStr) {
      this.searching = true;

      searchStr = searchStr.trim().toLowerCase();
      this.filter = searchStr;

      this.searching = false;
    },
    toggleRename(source) {
      this.$store.commit("source/edit", {
        s: source,
        obj: { edit: !source.edit },
      });
    },
    async _delete(source) {
      this.$store.dispatch("source/delete", source);
      this.showDelete = false;
    },

    async rename(source, text) {
      this.saving = true;
      text = text.trim();

      await postApi("set/sourceName", {
        name: text,
        sourceUID: source.uid,
      });

      this.$store.commit("source/edit", {
        s: source,
        obj: { name: text, edit: false },
      });

      this.saving = false;
    },
  },
};
</script>
